class CollapsibleContent extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;

    this.handleToggle = this.handleToggle.bind(this);
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.collapsible-content');

    if (!this.details || !this.summary || !this.content) {
      console.warn('<collapsible-content> is missing expected inner structure.');
      return;
    }

    // Set performance hints
    this.content.style.willChange = 'height';
    this.content.style.overflow = 'hidden';
    this.content.style.transition = 'height 0.3s ease';

    this.updateContentHeight();

    // Start closed
    this.content.style.height = '0px';
    this.details.removeAttribute('open');

    // Listeners
    this.summary.addEventListener('click', this.handleToggle);
    this.content.addEventListener('transitionend', this.handleTransitionEnd);
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    this.summary.removeEventListener('click', this.handleToggle);
    this.content.removeEventListener('transitionend', this.handleTransitionEnd);
    window.removeEventListener('resize', this.handleResize);
  }

  updateContentHeight() {
    // Temporarily show to measure height
    const prevHeight = this.content.style.height;
    const prevDisplay = this.content.style.display;

    this.content.style.display = 'block';
    this.content.style.height = 'auto';

    const fullHeight = this.content.scrollHeight;

    this.style.setProperty('--content-height', `${fullHeight}px`);

    // Restore original state
    this.content.style.height = prevHeight;
    this.content.style.display = prevDisplay;
  }

  handleToggle(event) {
    event.preventDefault();

    if (this.isAnimating) return;

    const isOpen = this.details.hasAttribute('open');

    if (isOpen) {
      this.classList.remove("is-open");
      this.closeAccordion();
    } else {
      this.classList.add("is-open");
      this.openAccordion();
    }
  }

  openAccordion() {
    this.isAnimating = true;
    this.details.setAttribute('open', ''); // For semantic state or styling
    this.updateContentHeight();
    const height = this.content.scrollHeight;

    this.content.style.height = `${height}px`;
  }

  closeAccordion() {
    this.isAnimating = true;
    const height = this.content.scrollHeight;

    this.content.style.height = `${height}px`; // Force reflow
    requestAnimationFrame(() => {
      this.content.style.height = '0px';
    });
  }

  handleTransitionEnd() {
    const isOpen = this.details.hasAttribute('open');

    if (isOpen && this.content.style.height !== '0px') {
      this.content.style.height = 'auto'; // Let it flow naturally
    } else {
      this.details.removeAttribute('open');
    }

    this.isAnimating = false;
  }

  handleResize() {
    if (this.details.hasAttribute('open')) {
      this.updateContentHeight();
      const height = this.content.scrollHeight;
      this.content.style.height = `${height}px`;
    } else {
      this.updateContentHeight(); // just update variable
    }
  }
}

customElements.define('collapsible-content', CollapsibleContent);

class AIBlock extends CollapsibleContent {
  constructor() {
    // console.log('constructor');
    super();
    this.originalNodes = [];
    this.charDelay = 20;
    this.hasTyped = false;
    this.measuredHeight = 0;
    // Removed: this.shouldClose = false;
  }

  connectedCallback() {
    // console.log('connectedCallback');
    super.connectedCallback();

    this.innerContent = this.querySelector('.accordion__content--inner');
    if (!this.innerContent) return;

    this.originalNodes = Array.from(this.innerContent.childNodes);
    this.measuredHeight = this.measureFullContentHeight(this.originalNodes);

    this.style.setProperty('--content-height', `${this.measuredHeight}px`);
    this.innerContent.innerHTML = '';
  }

  measureFullContentHeight(nodes) {
    // console.log('measureFullContentHeight');
    const tempWrapper = document.createElement('div');
    tempWrapper.style.cssText = `
      visibility: hidden;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      pointer-events: none;
      z-index: -1;
    `;
    const fakeInner = document.createElement('div');
    fakeInner.classList.add('accordion__content--inner');

    nodes.forEach(node => fakeInner.appendChild(node.cloneNode(true)));
    tempWrapper.appendChild(fakeInner);
    this.content.appendChild(tempWrapper);

    const height = fakeInner.offsetHeight;
    this.content.removeChild(tempWrapper);
    return height;
  }

  async openAccordion() {
    // console.log('openAccordion');
    if (this.details.hasAttribute('open')) return;
  
    this.isAnimating = true;
    this.details.setAttribute('open', '');
    this.content.style.height = `${this.measuredHeight}px`;
  
    if (!this.hasTyped) {
      this.innerContent.innerHTML = '';
      await this.typeContent(this.originalNodes);
      this.hasTyped = true;
      // Set height to auto after typing is complete
      this.content.style.height = 'auto';
    }
    // Don't set to auto immediately for already-typed content
    // Let the transition finish first, then handleTransitionEnd will handle it
  }

  closeAccordion() {
    this.isAnimating = true;
    const height = this.content.scrollHeight;
  
    this.content.style.height = `${height}px`; // Force reflow
    requestAnimationFrame(() => {
      this.content.style.height = '0px';
    });
  
    // Removed: this.shouldClose = false;
  }

  closeAccordion() {
    this.isAnimating = true;
    const height = this.content.scrollHeight;

    this.content.style.height = `${height}px`; // Force reflow
    requestAnimationFrame(() => {
      this.content.style.height = '0px';
    });

    this.shouldClose = false;
  }

  async typeContent(nodes) {
    // console.log('typeContent');
    for (const node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        await this.typeText(node.textContent, this.innerContent);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const clone = node.cloneNode(false);
        this.innerContent.appendChild(clone);
        if (node.childNodes.length > 0) {
          await this.typeContentInto(node.childNodes, clone);
        }
      }
    }
  }

  async typeContentInto(nodes, container) {
    // console.log('typeContentInto');
    for (const node of nodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        await this.typeText(node.textContent, container);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const clone = node.cloneNode(false);
        container.appendChild(clone);
        if (node.childNodes.length > 0) {
          await this.typeContentInto(node.childNodes, clone);
        }
      }
    }
  }

  async typeText(text, container) {
    // console.log('typeText');
    for (const char of text) {
      container.append(char);
      await this.delay(this.charDelay);
    }
  }

  delay(ms) {
    // console.log('delay');
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  handleTransitionEnd() {
    // console.log('handleTransitionEnd');
    const isOpen = this.details.hasAttribute('open');
    const currentHeight = this.content.style.height;
  
    // console.log('isOpen', isOpen);
    // console.log('currentHeight', currentHeight);
  
    if (isOpen && currentHeight !== '0px') {
      // Opening transition finished
      if (this.hasTyped) {
        // If content is already typed, set to auto for natural flow
        this.content.style.height = 'auto';
      }
      // If not typed yet, keep fixed height during typing
      // (auto will be set after typing completes in openAccordion)
    } else if (currentHeight === '0px') {
      // Closing transition finished - remove the open attribute
      this.details.removeAttribute('open');
    }
  
    this.isAnimating = false;
  }

  handleResize() {
    // console.log('handleResize');
    this.measuredHeight = this.measureFullContentHeight(this.originalNodes);
    this.style.setProperty('--content-height', `${this.measuredHeight}px`);
    if (this.details.hasAttribute('open')) {
      this.content.style.height = `${this.measuredHeight}px`;
    }
  }
}

customElements.define('ai-block', AIBlock);
